import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import './ProductPage.css';
import regionMap from './utils/regionMap';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

const toNum = (v) => (typeof v === 'number' && isFinite(v) ? v : null);

function nextDateStr(d) {
  const base = d ? new Date(d) : new Date();
  if (isNaN(base.getTime())) return new Date().toISOString().slice(0, 10);
  const plus = new Date(base);
  plus.setDate(base.getDate() + 1);
  return plus.toISOString().slice(0, 10);
}

function clipOrExtendHistoryToRank(historyArr, region, currentGlobalRank, currentRegionalRank) {
  if (!Array.isArray(historyArr) || historyArr.length === 0) return [];

  const isGlobal = !region || region === 'global';
  const targetGlobal = toNum(currentGlobalRank);
  const targetRegional = !isGlobal ? toNum(currentRegionalRank) : null;

  let idxGlobal = -1;
  let idxRegional = -1;

  for (let i = historyArr.length - 1; i >= 0; i--) {
    const row = historyArr[i];
    const g = toNum(row.globalRank);
    const r = row?.regionalRank ? toNum(row.regionalRank[region]) : null;

    if (idxGlobal < 0 && targetGlobal !== null && g === targetGlobal) idxGlobal = i;
    if (idxRegional < 0 && targetRegional !== null && r === targetRegional) idxRegional = i;

    if ((idxGlobal >= 0 || targetGlobal === null) && (idxRegional >= 0 || targetRegional === null)) break;
  }

  const indices = [idxGlobal, idxRegional].filter(i => i >= 0);
  if (indices.length > 0 && (
      (targetGlobal === null || idxGlobal >= 0) &&
      (targetRegional === null || idxRegional >= 0)
    )) {
    const upTo = Math.max(...indices);
    return historyArr.slice(0, upTo + 1);
  }

  const last = historyArr[historyArr.length - 1];
  const date = nextDateStr(last?.date);

  const newRow = {
    date,
    globalRank: targetGlobal !== null ? targetGlobal : toNum(last?.globalRank),
    regionalRank: { ...(last?.regionalRank || {}) }
  };
  if (!isGlobal && targetRegional !== null) {
    newRow.regionalRank[region] = targetRegional;
  }

  return [...historyArr, newRow];
}

function buildTrendSeries(historyArr, region) {
  if (!Array.isArray(historyArr)) return { labels: [], global: [], regional: [] };
  const labels = [];
  const global = [];
  const regional = [];

  for (const row of historyArr) {
    labels.push(row.date);
    global.push(toNum(row.globalRank));
    if (region && region !== 'global') {
      const r = row?.regionalRank ? toNum(row.regionalRank[region]) : null;
      regional.push(r);
    }
  }
  return { labels, global, regional };
}

function computeYDomain(global, regional) {
  const nums = [...global, ...regional].filter(v => typeof v === 'number' && isFinite(v));
  if (nums.length === 0) return { min: 1, max: 100 };
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const pad = Math.max(1, Math.round((max - min) * 0.05));
  return { min: Math.max(1, min - pad), max: max + pad };
}

function rankLabel(v) {
  return (typeof v === 'number' && isFinite(v)) ? `#${v}` : '—';
}

/* -------------------------- HOME NAV (fix) --------------------------------- */
function goHome(e) {
  try {
    if (e) e.preventDefault();
    const targetPath = '/global';
    const targetUrl = new URL(targetPath, window.location.origin).toString();
    const currPath = window.location.pathname || '';

    if (currPath.replace(/\/+$/, '') === targetPath) {
      window.location.reload();
      return;
    }

    window.location.assign(targetUrl);
  } catch {
    window.location.href = '/global';
  }
}

export default function ProductPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialRegion = (searchParams.get('region') || 'global').toLowerCase();
  const initialView = (searchParams.get('v') === 'bestseller') ? 'bestseller' : 'recommended';

  const [region, setRegion] = useState(initialRegion);
  const [viewMode, setViewMode] = useState(initialView);

  const [productsGlobal, setProductsGlobal] = useState([]);
  const [recommendedHistory, setRecommendedHistory] = useState(null);
  const [bestsellerHistory, setBestsellerHistory] = useState(null);

  const [currentRegionalRank, setCurrentRegionalRank] = useState(null);

  useEffect(() => {
    const next = {};
    if (region && region !== 'global') next.region = region;
    if (region !== 'global' && viewMode === 'bestseller') next.v = 'bestseller';
    setSearchParams(next, { replace: true });
  }, [region, viewMode, setSearchParams]);

  useEffect(() => {
    Promise.all([
      fetch('/data/latest-global.json').then(r => r.json()),
      fetch('/data/global/latest-global-with-change.json').then(r => r.json()).catch(() => [])
    ]).then(([base, change]) => {
      const changeMap = {};
      (change || []).forEach(i => {
        const key = i.productId?.trim().toLowerCase();
        if (key) changeMap[key] = i;
      });
      const normalized = (base || []).map(i => {
        const key = i.productId?.trim().toLowerCase();
        return { ...i, productId: key, ...(changeMap[key] || {}) };
      });
      setProductsGlobal(normalized);
    }).catch(e => console.error('❌ Failed to load global list', e));
  }, []);

  useEffect(() => {
    fetch('/data/historical-trends.json')
      .then(r => r.json())
      .then(setRecommendedHistory)
      .catch(() => setRecommendedHistory(null));

    fetch('/data/best-sellers/historical-trends.json')
      .then(r => r.json())
      .then(setBestsellerHistory)
      .catch(() => setBestsellerHistory(null));
  }, []);

  useEffect(() => {
    const productId = (id || '').trim().toLowerCase();
    if (!productId) return;

    if (!region || region === 'global') {
      setCurrentRegionalRank(null);
      return;
    }

    const url = viewMode === 'bestseller'
      ? `/data/best-sellers/by-region/latest-${region}.json`
      : `/data/by-region/latest-${region}.json`;

    fetch(url)
      .then(r => r.json())
      .then(list => {
        const item = (Array.isArray(list) ? list : []).find(
          x => (x.productId || '').trim().toLowerCase() === productId
        );
        const rnk = item && typeof item.rank === 'number' ? item.rank : null;
        setCurrentRegionalRank(rnk);
      })
      .catch(() => setCurrentRegionalRank(null));
  }, [id, region, viewMode]);

  const product = useMemo(() => {
    const key = (id || '').trim().toLowerCase();
    return productsGlobal.find(p => (p.productId || '').toLowerCase() === key) || null;
  }, [id, productsGlobal]);

  const fullHistory = useMemo(() => {
    const h = viewMode === 'bestseller' ? bestsellerHistory : recommendedHistory;
    const key = (id || '').trim().toLowerCase();
    if (!h || !key) return [];
    const arr = h[key];
    return Array.isArray(arr) ? arr : [];
  }, [id, recommendedHistory, bestsellerHistory, viewMode]);

  const currentGlobalRank = product && typeof product.globalRank === 'number' ? product.globalRank : null;

  const adjustedHistory = useMemo(
    () => clipOrExtendHistoryToRank(fullHistory, region, currentGlobalRank, currentRegionalRank),
    [fullHistory, region, currentGlobalRank, currentRegionalRank]
  );

  const { labels, global, regional: regionalSeries } = useMemo(
    () => buildTrendSeries(adjustedHistory, region),
    [adjustedHistory, region]
  );

  const { min, max } = useMemo(
    () => computeYDomain(global, regionalSeries),
    [global, regionalSeries]
  );

  const showRegional = region && region !== 'global';

  const availableRegionCodes = useMemo(() => {
    const addFromHistory = (hist, set) => {
      if (!hist) return;
      const key = id?.trim().toLowerCase();
      const rows = Array.isArray(hist[key]) ? hist[key] : [];
      for (const row of rows) {
        const rr = row?.regionalRank;
        if (rr && typeof rr === 'object') {
          for (const code of Object.keys(rr)) {
            if (typeof rr[code] === 'number') set.add(code.toLowerCase());
          }
        }
      }
    };

    const set = new Set();
    addFromHistory(recommendedHistory, set);
    addFromHistory(bestsellerHistory, set);

    if (region !== 'global' && currentRegionalRank != null) {
      set.add(region.toLowerCase());
    }

    return Array.from(set).sort();
  }, [id, recommendedHistory, bestsellerHistory, region, currentRegionalRank]);

  const chartData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'Global Rank',
        data: global,
        tension: 0.25,
        pointRadius: 2,
        borderWidth: 2,
        spanGaps: true,
      },
      ...(showRegional ? [{
        label: `${regionMap[region] || region} Rank`,
        data: regionalSeries,
        tension: 0.25,
        pointRadius: 2,
        borderWidth: 2,
        spanGaps: true,
      }] : []),
    ],
  }), [labels, global, regionalSeries, region, showRegional]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      title: { display: false },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${rankLabel(ctx.parsed.y)}`
        }
      }
    },
    scales: {
      y: {
        reverse: true,
        suggestedMin: min,
        suggestedMax: max,
        ticks: { precision: 0 }
      },
      x: { ticks: { maxRotation: 0, autoSkip: true } }
    },
    interaction: { mode: 'nearest', intersect: false }
  }), [min, max]);

  const latest = adjustedHistory[adjustedHistory.length - 1] || {};
  const prev = adjustedHistory[adjustedHistory.length - 2] || null;
  const globalDelta = (prev && typeof latest.globalRank === 'number' && typeof prev.globalRank === 'number')
    ? prev.globalRank - latest.globalRank : 0;
  const regionalDelta = (showRegional && prev)
    ? ((prev.regionalRank?.[region] ?? null) - (latest.regionalRank?.[region] ?? null))
    : 0;

  const setRegionAndSync = (code) => {
    setRegion(code);
    if (code === 'global') setViewMode('recommended');
  };

  return (
    <div className="product-page">
      <header className="pp-header">
        <Link to={region === 'global' ? '/global' : `/region/${region}`} className="pp-back">← Back</Link>

        {/* ✅ Product page logo is intentionally smaller (pp-logo) */}
        <a
          href="/global"
          onClick={goHome}
          className="logoLink"
          aria-label="POPDAQ Home"
          style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', pointerEvents: 'auto' }}
        >
          <img
            src="/directory/popdaqArtboard1@4x.png"
            alt="POPDAQ"
            className="pp-logo"
          />
        </a>
      </header>

      <main className="pp-main">
        <section className="pp-title">
          <div className="pp-brand">{product?.brand || '—'}</div>
          <h1 className="pp-name">{product?.name || 'Product'}</h1>
        </section>

        <section className="pp-controls">
          <div className="pp-availability">
            <span className="pp-availability-label">Available in:</span>
            <button
              className={`chip ${region === 'global' ? 'active' : ''}`}
              onClick={() => setRegionAndSync('global')}
            >
              Global
            </button>
            {availableRegionCodes.map(code => (
              <button
                key={code}
                className={`chip ${region === code ? 'active' : ''}`}
                onClick={() => setRegionAndSync(code)}
                title={regionMap[code] || code.toUpperCase()}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          {/* keep this placement exactly as you like it */}
          {region !== 'global' && (
            <div className="pp-toggle">
              <button
                className={viewMode === 'recommended' ? 'active' : ''}
                onClick={() => setViewMode('recommended')}
              >
                Recommended
              </button>
              <button
                className={viewMode === 'bestseller' ? 'active' : ''}
                onClick={() => setViewMode('bestseller')}
              >
                Best Sellers
              </button>
            </div>
          )}
        </section>

        <section className="pp-body">
          {/* LEFT: card block */}
          <div className="pp-left">
            <div className="pp-card">
              <div className="pp-cardMedia">
                {product?.image && (
                  <img className="pp-image" src={product.image} alt={product?.name} />
                )}
              </div>

              <div className="pp-cardInfo">
                <div className="pp-meta">
                  {product?.price && <div className="pp-price">{product.price}</div>}
                  {product?.status && <div className="pp-status">{product.status}</div>}
                </div>

                <div className="pp-ranks">
                  <div className="pp-rankline">
                    🌐 Global Rank: <strong>#{product?.globalRank ?? latest.globalRank ?? '—'}</strong>
                    <span className={`pp-delta ${globalDelta > 0 ? 'up' : globalDelta < 0 ? 'down' : ''}`}>
                      {globalDelta > 0 ? '▲' : globalDelta < 0 ? '▼' : '—'} {Math.abs(globalDelta)}
                    </span>
                  </div>

                  {region !== 'global' && (
                    <div className="pp-rankline">
                      📍 {regionMap[region] || region}: <strong>#{latest?.regionalRank?.[region] ?? '—'}</strong>
                      <span className={`pp-delta ${regionalDelta > 0 ? 'up' : regionalDelta < 0 ? 'down' : ''}`}>
                        {regionalDelta > 0 ? '▲' : regionalDelta < 0 ? '▼' : '—'} {Math.abs(regionalDelta)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: chart block */}
          <div className="pp-right">
            <div className="pp-chartwrap">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </section>
      </main>

      <footer className="pp-footer">
        <p>Powered by POPDAQ</p>
      </footer>
    </div>
  );
}

// import React, { useEffect, useMemo, useState } from 'react';
// import { useParams, useSearchParams, Link } from 'react-router-dom';
// import './ProductPage.css';
// import regionMap from './utils/regionMap';

// import {
//   Chart as ChartJS,
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Title,
//   Tooltip as ChartTooltip,
//   Legend,
// } from 'chart.js';
// import { Line } from 'react-chartjs-2';

// ChartJS.register(
//   CategoryScale,
//   LinearScale,
//   PointElement,
//   LineElement,
//   Title,
//   ChartTooltip,
//   Legend
// );

// const toNum = (v) => (typeof v === 'number' && isFinite(v) ? v : null);

// function nextDateStr(d) {
//   const base = d ? new Date(d) : new Date();
//   if (isNaN(base.getTime())) return new Date().toISOString().slice(0, 10);
//   const plus = new Date(base);
//   plus.setDate(base.getDate() + 1);
//   return plus.toISOString().slice(0, 10);
// }

// function clipOrExtendHistoryToRank(historyArr, region, currentGlobalRank, currentRegionalRank) {
//   if (!Array.isArray(historyArr) || historyArr.length === 0) return [];

//   const isGlobal = !region || region === 'global';
//   const targetGlobal = toNum(currentGlobalRank);
//   const targetRegional = !isGlobal ? toNum(currentRegionalRank) : null;

//   let idxGlobal = -1;
//   let idxRegional = -1;

//   for (let i = historyArr.length - 1; i >= 0; i--) {
//     const row = historyArr[i];
//     const g = toNum(row.globalRank);
//     const r = row?.regionalRank ? toNum(row.regionalRank[region]) : null;

//     if (idxGlobal < 0 && targetGlobal !== null && g === targetGlobal) idxGlobal = i;
//     if (idxRegional < 0 && targetRegional !== null && r === targetRegional) idxRegional = i;

//     if ((idxGlobal >= 0 || targetGlobal === null) && (idxRegional >= 0 || targetRegional === null)) {
//       break;
//     }
//   }

//   const indices = [idxGlobal, idxRegional].filter(i => i >= 0);
//   if (indices.length > 0 && (
//       (targetGlobal === null || idxGlobal >= 0) &&
//       (targetRegional === null || idxRegional >= 0)
//     )) {
//     const upTo = Math.max(...indices);
//     return historyArr.slice(0, upTo + 1);
//   }

//   const last = historyArr[historyArr.length - 1];
//   const date = nextDateStr(last?.date);

//   const newRow = {
//     date,
//     globalRank: targetGlobal !== null ? targetGlobal : toNum(last?.globalRank),
//     regionalRank: { ...(last?.regionalRank || {}) }
//   };
//   if (!isGlobal && targetRegional !== null) {
//     newRow.regionalRank[region] = targetRegional;
//   }

//   return [...historyArr, newRow];
// }

// function buildTrendSeries(historyArr, region) {
//   if (!Array.isArray(historyArr)) return { labels: [], global: [], regional: [] };
//   const labels = [];
//   const global = [];
//   const regional = [];

//   for (const row of historyArr) {
//     labels.push(row.date);
//     global.push(toNum(row.globalRank));
//     if (region && region !== 'global') {
//       const r = row?.regionalRank ? toNum(row.regionalRank[region]) : null;
//       regional.push(r);
//     }
//   }
//   return { labels, global, regional };
// }

// function computeYDomain(global, regional) {
//   const nums = [...global, ...regional].filter(v => typeof v === 'number' && isFinite(v));
//   if (nums.length === 0) return { min: 1, max: 100 };
//   const min = Math.min(...nums);
//   const max = Math.max(...nums);
//   const pad = Math.max(1, Math.round((max - min) * 0.05));
//   return { min: Math.max(1, min - pad), max: max + pad };
// }

// function rankLabel(v) {
//   return (typeof v === 'number' && isFinite(v)) ? `#${v}` : '—';
// }

// /* -------------------------- HOME NAV (fix) --------------------------------- */
// function goHome(e) {
//   try {
//     if (e) e.preventDefault();
//     const targetPath = '/global';
//     const targetUrl = new URL(targetPath, window.location.origin).toString();
//     const currPath = window.location.pathname || '';

//     if (currPath.replace(/\/+$/, '') === targetPath) {
//       window.location.reload();
//       return;
//     }

//     window.location.assign(targetUrl);
//   } catch {
//     window.location.href = '/global';
//   }
// }

// export default function ProductPage() {
//   const { id } = useParams();
//   const [searchParams, setSearchParams] = useSearchParams();

//   const initialRegion = (searchParams.get('region') || 'global').toLowerCase();
//   const initialView = (searchParams.get('v') === 'bestseller') ? 'bestseller' : 'recommended';

//   const [region, setRegion] = useState(initialRegion);
//   const [viewMode, setViewMode] = useState(initialView);

//   const [productsGlobal, setProductsGlobal] = useState([]);
//   const [recommendedHistory, setRecommendedHistory] = useState(null);
//   const [bestsellerHistory, setBestsellerHistory] = useState(null);

//   const [currentRegionalRank, setCurrentRegionalRank] = useState(null);

//   useEffect(() => {
//     const next = {};
//     if (region && region !== 'global') next.region = region;
//     if (region !== 'global' && viewMode === 'bestseller') next.v = 'bestseller';
//     setSearchParams(next, { replace: true });
//   }, [region, viewMode, setSearchParams]);

//   useEffect(() => {
//     Promise.all([
//       fetch('/data/latest-global.json').then(r => r.json()),
//       fetch('/data/global/latest-global-with-change.json').then(r => r.json()).catch(() => [])
//     ]).then(([base, change]) => {
//       const changeMap = {};
//       (change || []).forEach(i => {
//         const key = i.productId?.trim().toLowerCase();
//         if (key) changeMap[key] = i;
//       });
//       const normalized = (base || []).map(i => {
//         const key = i.productId?.trim().toLowerCase();
//         return { ...i, productId: key, ...(changeMap[key] || {}) };
//       });
//       setProductsGlobal(normalized);
//     }).catch(e => console.error('❌ Failed to load global list', e));
//   }, []);

//   useEffect(() => {
//     fetch('/data/historical-trends.json')
//       .then(r => r.json())
//       .then(setRecommendedHistory)
//       .catch(() => setRecommendedHistory(null));

//     fetch('/data/best-sellers/historical-trends.json')
//       .then(r => r.json())
//       .then(setBestsellerHistory)
//       .catch(() => setBestsellerHistory(null));
//   }, []);

//   useEffect(() => {
//     const productId = (id || '').trim().toLowerCase();
//     if (!productId) return;

//     if (!region || region === 'global') {
//       setCurrentRegionalRank(null);
//       return;
//     }

//     const url = viewMode === 'bestseller'
//       ? `/data/best-sellers/by-region/latest-${region}.json`
//       : `/data/by-region/latest-${region}.json`;

//     fetch(url)
//       .then(r => r.json())
//       .then(list => {
//         const item = (Array.isArray(list) ? list : []).find(
//           x => (x.productId || '').trim().toLowerCase() === productId
//         );
//         const rnk = item && typeof item.rank === 'number' ? item.rank : null;
//         setCurrentRegionalRank(rnk);
//       })
//       .catch(() => setCurrentRegionalRank(null));
//   }, [id, region, viewMode]);

//   const product = useMemo(() => {
//     const key = (id || '').trim().toLowerCase();
//     return productsGlobal.find(p => (p.productId || '').toLowerCase() === key) || null;
//   }, [id, productsGlobal]);

//   const fullHistory = useMemo(() => {
//     const h = viewMode === 'bestseller' ? bestsellerHistory : recommendedHistory;
//     const key = (id || '').trim().toLowerCase();
//     if (!h || !key) return [];
//     const arr = h[key];
//     return Array.isArray(arr) ? arr : [];
//   }, [id, recommendedHistory, bestsellerHistory, viewMode]);

//   const currentGlobalRank = product && typeof product.globalRank === 'number' ? product.globalRank : null;

//   const adjustedHistory = useMemo(
//     () => clipOrExtendHistoryToRank(fullHistory, region, currentGlobalRank, currentRegionalRank),
//     [fullHistory, region, currentGlobalRank, currentRegionalRank]
//   );

//   const { labels, global, regional: regionalSeries } = useMemo(
//     () => buildTrendSeries(adjustedHistory, region),
//     [adjustedHistory, region]
//   );

//   const { min, max } = useMemo(
//     () => computeYDomain(global, regionalSeries),
//     [global, regionalSeries]
//   );

//   const showRegional = region && region !== 'global';

//   const availableRegionCodes = useMemo(() => {
//     const addFromHistory = (hist, set) => {
//       if (!hist) return;
//       const key = id?.trim().toLowerCase();
//       const rows = Array.isArray(hist[key]) ? hist[key] : [];
//       for (const row of rows) {
//         const rr = row?.regionalRank;
//         if (rr && typeof rr === 'object') {
//           for (const code of Object.keys(rr)) {
//             if (typeof rr[code] === 'number') set.add(code.toLowerCase());
//           }
//         }
//       }
//     };

//     const set = new Set();
//     addFromHistory(recommendedHistory, set);
//     addFromHistory(bestsellerHistory, set);

//     if (region !== 'global' && currentRegionalRank != null) {
//       set.add(region.toLowerCase());
//     }

//     return Array.from(set).sort();
//   }, [id, recommendedHistory, bestsellerHistory, region, currentRegionalRank]);

//   const chartData = useMemo(() => ({
//     labels,
//     datasets: [
//       {
//         label: 'Global Rank',
//         data: global,
//         tension: 0.25,
//         pointRadius: 2,
//         borderWidth: 2,
//         spanGaps: true,
//       },
//       ...(showRegional ? [{
//         label: `${regionMap[region] || region} Rank`,
//         data: regionalSeries,
//         tension: 0.25,
//         pointRadius: 2,
//         borderWidth: 2,
//         spanGaps: true,
//       }] : []),
//     ],
//   }), [labels, global, regionalSeries, region, showRegional]);

//   const options = useMemo(() => ({
//     responsive: true,
//     maintainAspectRatio: false,
//     plugins: {
//       legend: { display: true },
//       title: { display: false },
//       tooltip: {
//         mode: 'nearest',
//         intersect: false,
//         callbacks: {
//           label: (ctx) => `${ctx.dataset.label}: ${rankLabel(ctx.parsed.y)}`
//         }
//       }
//     },
//     scales: {
//       y: {
//         reverse: true,
//         suggestedMin: min,
//         suggestedMax: max,
//         ticks: { precision: 0 }
//       },
//       x: { ticks: { maxRotation: 0, autoSkip: true } }
//     },
//     interaction: { mode: 'nearest', intersect: false }
//   }), [min, max]);

//   const latest = adjustedHistory[adjustedHistory.length - 1] || {};
//   const prev = adjustedHistory[adjustedHistory.length - 2] || null;
//   const globalDelta = (prev && typeof latest.globalRank === 'number' && typeof prev.globalRank === 'number')
//     ? prev.globalRank - latest.globalRank : 0;
//   const regionalDelta = (showRegional && prev)
//     ? ((prev.regionalRank?.[region] ?? null) - (latest.regionalRank?.[region] ?? null))
//     : 0;

//   const setRegionAndSync = (code) => {
//     setRegion(code);
//     if (code === 'global') {
//       setViewMode('recommended');
//     }
//   };

//   return (
//     <div className="product-page">
//       <header className="pp-header">
//         <Link to={region === 'global' ? '/global' : `/region/${region}`} className="pp-back">← Back</Link>

//         {/* ✅ FIXED: always works; reloads when already on /global */}
//         <a
//           href="/global"
//           onClick={goHome}
//           className="logoLink"
//           aria-label="POPDAQ Home"
//           style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', pointerEvents: 'auto' }}
//         >
//           <img src="/directory/popdaqArtboard1@4x.png" alt="POPDAQ" className="logo" />
//         </a>
//       </header>

//       <main className="pp-main">
//         <section className="pp-title">
//           <div className="pp-brand">{product?.brand || '—'}</div>
//           <h1 className="pp-name">{product?.name || 'Product'}</h1>
//         </section>

//         <section className="pp-controls">
//           <div className="pp-availability">
//             <span className="pp-availability-label">Available in:</span>
//             <button
//               className={`chip ${region === 'global' ? 'active' : ''}`}
//               onClick={() => setRegionAndSync('global')}
//             >
//               Global
//             </button>
//             {availableRegionCodes.map(code => (
//               <button
//                 key={code}
//                 className={`chip ${region === code ? 'active' : ''}`}
//                 onClick={() => setRegionAndSync(code)}
//                 title={regionMap[code] || code.toUpperCase()}
//               >
//                 {code.toUpperCase()}
//               </button>
//             ))}
//           </div>

//           {region !== 'global' && (
//             <div className="pp-toggle">
//               <button
//                 className={viewMode === 'recommended' ? 'active' : ''}
//                 onClick={() => setViewMode('recommended')}
//               >
//                 Recommended
//               </button>
//               <button
//                 className={viewMode === 'bestseller' ? 'active' : ''}
//                 onClick={() => setViewMode('bestseller')}
//               >
//                 Best Sellers
//               </button>
//             </div>
//           )}
//         </section>

//         <section className="pp-body">
//           <div className="pp-left">
//             {product?.image && <img className="pp-image" src={product.image} alt={product?.name} />}
//             <div className="pp-meta">
//               {product?.price && <div className="pp-price">{product.price}</div>}
//               {product?.status && <div className="pp-status">{product.status}</div>}
//             </div>

//             <div className="pp-ranks">
//               <div className="pp-rankline">
//                 🌐 Global Rank: <strong>#{product?.globalRank ?? latest.globalRank ?? '—'}</strong>
//                 <span className={`pp-delta ${globalDelta>0?'up':globalDelta<0?'down':''}`}>
//                   {globalDelta>0 ? '▲' : globalDelta<0 ? '▼' : '—'} {Math.abs(globalDelta)}
//                 </span>
//               </div>
//               {region !== 'global' && (
//                 <div className="pp-rankline">
//                   📍 {regionMap[region] || region}: <strong>#{latest?.regionalRank?.[region] ?? '—'}</strong>
//                   <span className={`pp-delta ${regionalDelta>0?'up':regionalDelta<0?'down':''}`}>
//                     {regionalDelta>0 ? '▲' : regionalDelta<0 ? '▼' : '—'} {Math.abs(regionalDelta)}
//                   </span>
//                 </div>
//               )}
//             </div>
//           </div>

//           <div className="pp-right">
//             <div className="pp-chartwrap">
//               <Line data={chartData} options={options} />
//             </div>
//           </div>
//         </section>
//       </main>

//       <footer className="pp-footer">
//         <p>Powered by POPDAQ</p>
//       </footer>
//     </div>
//   );
// }
